// WHY a task's last failure happened — the closed `tasks.failure_kind`
// vocabulary, DERIVED from one row per kind (`./failure-kinds/`). See `../task.ts`
// for the file docblock.

import { z } from 'zod';
import { BOX_FAILURE_KINDS } from './failure-kinds/box';
import { PROVIDER_FAILURE_KINDS } from './failure-kinds/provider';
import type { FailureKindCopy, FailureKindRow } from './failure-kinds/row';
import { RUN_FAILURE_KINDS } from './failure-kinds/run';
import { TREE_FAILURE_KINDS } from './failure-kinds/tree';

/**
 * Every failure kind's row, from the four families. A spread silently keeps the LAST of
 * two equal keys, so `./failure-kinds/registry.test.ts` holds keys and values unique
 * across families.
 */
const ROWS = {
  ...PROVIDER_FAILURE_KINDS,
  ...BOX_FAILURE_KINDS,
  ...TREE_FAILURE_KINDS,
  ...RUN_FAILURE_KINDS,
};
type Rows = typeof ROWS;

/**
 * Every row, in `ordinal` order. Load-bearing: `TaskFailureKindSchema.options` is in this
 * order, and the MCP catalog and the JSON schemas render the enum in it, so re-ordering
 * is a byte change on a public contract. Each row states its own `ordinal` rather than
 * inheriting a position from the family spread, so a new kind lands at the end whatever
 * family it joins: a new kind is ONE row with ordinal `n` (for `n` existing kinds) in one
 * family file, plus its `./failure-kinds/golden.test.ts` line. `./failure-kinds/registry.test.ts`
 * holds the ordinals unique and exactly `0..n-1`, and pins the shipped order.
 */
// `Object.entries` widens keys to `string`; the entries ARE `ROWS`' own, so the key type is exact.
const ORDERED = (Object.entries(ROWS) as [keyof Rows, FailureKindRow][]).sort(
  ([, a], [, b]) => a.ordinal - b.ordinal,
);

/**
 * WHY a task's last failure happened — the discriminated classification stored
 * on `tasks.failure_kind` when a failure lane touches the row (retry or
 * terminal). A closed vocabulary: {@link TASK_FAILURE_BUDGET} is total over it,
 * so adding a kind without deciding its budget AND its exhaustion action fails
 * typecheck — never a silent default toward retryable, and never a silent
 * default toward charging the task for a failure that was not its own.
 * {@link TASK_FAILURE_PRODUCER} is total over it too, so a kind that nothing
 * writes has to say so instead of looking like a kind that never fires.
 *
 * IT IS THE LAST FAILED ATTEMPT'S KIND, AND IT SURVIVES RECOVERY. Read without
 * THE NULLs ARE A FROZEN BACKLOG, NOT A LIVE WRITER, and that distinction is the
 * whole answer to "which write path fails a task without a kind". Prod on
 * 2026-09-01 carries 971 `failed` rows with a NULL here, which reads like a seam
 * that forgets — and every one of them is `attempt = 0` with an `updated_at`
 * between 2026-07-09 and 2026-08-15, stopping dead there. They predate the
 * column's producers. TODAY THERE IS NO SUCH PATH, and that is an enumeration
 * rather than an impression: `failOrRetryTask` takes a non-optional
 * `failureKind: TaskFailureKind`, and `rependStuckTask`, `reapStaleRunner`,
 * `failTasksWithDeadDependencies` and `failTasksWithDeadPlanDependency` each
 * hardcode theirs. The next writer is what needs guarding, since one omitting the
 * stamp compiles — so `packages/db/src/queries/task-failure-kind-write.test.ts`
 * reads every statement that lands `failed` on `tasks` and requires the kind in
 * the same clause. Backfilling the 971 is NOT owed: nothing recorded why they
 * died, and inventing a kind for them would be worse than the NULL that says so.
 *
 * Read without
 * a `status` filter it reads like corruption — prod carries `completed`,
 * `merged`, `cancelled` and `in_progress` rows all holding a `failure_kind` —
 * and it is not. The PROPERTY, not a count (this paragraph shipped one, and it
 * was stale inside a day): on prod 2026-08-29, of every row with `attempt >= 1`,
 * ZERO have a NULL here, across all five statuses those rows reach. That is what
 * "it survives recovery" means, and it is the invariant a reader can re-check —
 * `SELECT count(*) FILTER (WHERE failure_kind IS NULL) FROM tasks WHERE attempt
 * >= 1` must stay 0. A task that errors is re-pended WITH the stamp, a later
 * attempt succeeds, and no lane clears the column — deliberately, because "this
 * row failed twice before it landed" is a fact worth keeping and a cleared
 * column would erase it.
 *
 * So the ONE thing every consumer must do is filter: `failure_kind` answers
 * *"why did this row's last attempt fail"*, and only `WHERE status = 'failed'`
 * turns that into *"why is this row dead"*. A `GROUP BY status, failure_kind`
 * is the correct shape; a `GROUP BY failure_kind` alone over-counts every
 * terminal bucket by the tasks that recovered.
 */
// `Object.fromEntries` returns `Record<string, string>`; the pairs are each row's own key and
// `value`, so the literal-typed record is exact.
export const TaskFailureKind = Object.fromEntries(
  ORDERED.map(([key, row]) => [key, row.value]),
) as {
  readonly [K in keyof Rows]: Rows[K]['value'];
};
export const TaskFailureKindSchema = z.enum(TaskFailureKind);
export type TaskFailureKind = z.infer<typeof TaskFailureKindSchema>;

/**
 * A per-kind table, projected from the rows — TOTAL over {@link TaskFailureKind} by
 * construction, in {@link TaskFailureKindSchema}'s order. Every per-kind table the
 * platform reads is one of these, so no table can miss a kind the enum has.
 */
export function projectFailureKinds<T>(
  pick: (row: FailureKindRow, kind: TaskFailureKind) => T,
): Record<TaskFailureKind, T> {
  return Object.fromEntries(
    // A row's `value` is typed `string` on `FailureKindRow`; every row's value IS an enum
    // member (the enum is built from these same rows), and the record is total over it.
    ORDERED.map(([, row]) => [row.value, pick(row, row.value as TaskFailureKind)]),
  ) as Record<TaskFailureKind, T>;
}

const COPY = projectFailureKinds((row) => row.copy);
const BLAMES_BOX = projectFailureKinds((row) => row.blamesBox);
const RATE_EXCLUDED = projectFailureKinds((row) => row.failureRateExcluded);

/**
 * One per-kind lookup, loud on a miss. The type already forbids an unknown kind, but a
 * value can arrive past it (a raw DB column, a cast); indexing the table would hand back
 * `undefined` and the reader would throw a nameless TypeError further on.
 */
function lookup<T>(table: Record<TaskFailureKind, T>, kind: TaskFailureKind, reader: string): T {
  if (!Object.hasOwn(table, kind)) {
    throw new Error(
      `${reader}: ${JSON.stringify(kind)} is not a TaskFailureKind — narrow a raw value with narrowTaskFailureKind (wire/task.ts) before reading per-kind data.`,
    );
  }
  return table[kind];
}

/** What each surface tells a reader about a failure of this kind (`FailureKindCopy`). */
export function taskFailureCopy(kind: TaskFailureKind): FailureKindCopy {
  return lookup(COPY, kind, 'taskFailureCopy');
}

/**
 * ONE human sentence for a failure of this kind (#2574): a row at `attempt = 0` reads the
 * `unclaimed` arm, one that ran the `ran` arm; every row carries both, so the sentence is
 * total over the enum. The api's detail wire and `task outcome` both read it — outcome as
 * the reason of a failure no box wrote an `error` event for (a platform lane ended it).
 */
export function taskFailureSentence(kind: TaskFailureKind, attempt: number): string {
  const copy = taskFailureCopy(kind);
  return attempt === 0 ? copy.unclaimed : copy.ran;
}

/**
 * Would an operator go inspect THE BOX for a failure of this kind? `true` means the
 * machine is claiming health it does not have; everything else is the model, the
 * provider, the credential, the repo, or a thing we did on purpose. The gate on
 * `box_unhealthy`.
 */
export function taskFailureBlamesBox(kind: TaskFailureKind): boolean {
  return lookup(BLAMES_BOX, kind, 'taskFailureBlamesBox');
}

/** Is a failure of this kind kept out of the fleet failure-rate numerator? */
export function taskFailureRateExcluded(kind: TaskFailureKind): boolean {
  return lookup(RATE_EXCLUDED, kind, 'taskFailureRateExcluded');
}
